import { DragDropContext, DropResult } from '@hello-pangea/dnd';
import {
  createContext,
  useCallback,
  useContext,
  useState,
  ReactNode,
  useEffect,
  type JSX,
} from 'react';
import { useLearningCollection } from '../../../LearningCollectionContext';

interface UnifiedDragContextValue {
  isDragging: boolean;
}

const UnifiedDragContextState = createContext<UnifiedDragContextValue>({ isDragging: false });

export function useUnifiedDragContext(): UnifiedDragContextValue {
  return useContext(UnifiedDragContextState);
}

interface UnifiedDragProviderProps {
  children: ReactNode;
}

// Parse section index from droppableId format: "section-{index}-{displayType}"
function parseSectionIndex(droppableId: string): number | null {
  const match = droppableId.match(/section-(\d+)/);
  return match ? parseInt(match[1], 10) : null;
}

/**
 * UnifiedDragProvider wraps all sections to enable cross-section drag-and-drop.
 *
 * Each section's Droppable should use a unique droppableId in the format:
 * - "section-{index}-carousel" for carousel display
 * - "section-{index}-list" for list display
 * - "section-{index}-grid" for grid display
 */
export function UnifiedDragProvider({ children }: UnifiedDragProviderProps): JSX.Element {
  const { reorderProgramsInSection, moveProgramBetweenSections } = useLearningCollection();
  const [isDragging, setIsDragging] = useState(false);

  // Reset drag state on unmount to prevent stale state
  useEffect(() => {
    return () => {
      setIsDragging(false);
    };
  }, []);

  const handleDragStart = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      setIsDragging(false);

      if (!result.destination) return;

      const { source, destination } = result;
      const sourceSectionIndex = parseSectionIndex(source.droppableId);
      const destSectionIndex = parseSectionIndex(destination.droppableId);

      if (sourceSectionIndex === null || destSectionIndex === null) {
        console.warn('Invalid droppableId format:', {
          source: source.droppableId,
          destination: destination.droppableId,
        });
        return;
      }

      if (sourceSectionIndex === destSectionIndex) {
        if (source.index !== destination.index) {
          reorderProgramsInSection(sourceSectionIndex, source.index, destination.index);
        }
      } else {
        moveProgramBetweenSections(
          sourceSectionIndex,
          destSectionIndex,
          source.index,
          destination.index,
        );
      }
    },
    [reorderProgramsInSection, moveProgramBetweenSections],
  );

  return (
    <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <UnifiedDragContextState.Provider value={{ isDragging }}>
        {children}
      </UnifiedDragContextState.Provider>
    </DragDropContext>
  );
}
